import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { environment } from 'src/app/environments/environment';
import { AnalyticsMenuService } from '../admin-services/analytics-menu.service';

type DashboardErrorType =
  | 'access_denied'
  | 'unavailable'
  | 'stale'
  | 'disconnected'
  | 'generic'
  | null;

@Component({
  selector: 'app-admin-analytics-dashboard',
  template: `
    <div class="analytics-dashboard-container p-3">
      <div *ngIf="isLoading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status"></div>
        <p class="mt-3 text-muted mb-0">Loading dashboard...</p>
      </div>

      <div *ngIf="!isLoading && hasError" class="alert alert-warning mb-0">
        <div class="fw-semibold mb-2">Unable to load dashboard</div>
        <div class="text-muted mb-3">{{ errorMessage }}</div>
        <button class="btn btn-sm btn-primary me-2" (click)="reload()">
          Retry
        </button>
        <button
          *ngIf="errorType === 'stale' || errorType === 'disconnected'"
          class="btn btn-sm btn-outline-secondary"
          (click)="navigateToSettings()"
        >
          Analytics Settings
        </button>
      </div>

      <div
        *ngIf="!isLoading && !hasError && safeEmbedUrl"
        class="ratio"
        style="--bs-aspect-ratio: 70%;"
      >
        <iframe
          [src]="safeEmbedUrl"
          frameborder="0"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
          allow="fullscreen"
          title="TPM Analytics Dashboard"
        ></iframe>
      </div>
    </div>
  `
})
export class AnalyticsDashboardComponent implements OnInit, OnChanges {
  @Input() dashboardId = '';

  safeEmbedUrl: SafeResourceUrl | null = null;
  isLoading = true;
  errorType: DashboardErrorType = null;
  errorMessage = '';
  companyId = 0;

  constructor(
    private analyticsMenuService: AnalyticsMenuService,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.companyId = Number(this.getUser()?.companyID) || 0;
    this.loadDashboard();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dashboardId'] && !changes['dashboardId'].firstChange) {
      this.loadDashboard();
    }
  }

  reload(): void {
    this.loadDashboard();
  }

  navigateToSettings(): void {
    window.location.href = `${environment.companyWebBaseUrl}/company-portal/settings/integrations`;
  }

  get hasError(): boolean {
    return this.errorType !== null;
  }

  private loadDashboard(): void {
    this.isLoading = true;
    this.errorType = null;
    this.errorMessage = '';
    this.safeEmbedUrl = null;

    if (!this.dashboardId || !this.companyId) {
      this.errorType = 'generic';
      this.errorMessage = 'Dashboard is not configured.';
      this.isLoading = false;
      return;
    }

    this.analyticsMenuService
      .getDashboardEmbedUrl(this.companyId, this.dashboardId)
      .subscribe({
        next: (config) => {
          this.safeEmbedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
            config.embedUrl
          );
          this.isLoading = false;
        },
        error: (err) => {
          this.isLoading = false;
          if (err.status === 403) {
            this.errorType = 'access_denied';
            this.errorMessage = 'You do not have access to this dashboard.';
          } else if (err.status === 503 || err.status === 502) {
            this.errorType = 'unavailable';
            this.errorMessage = 'Analytics is temporarily unavailable.';
          } else if (err.status === 404) {
            const detail =
              `${err.error?.detail || err.error?.message || ''}`.toLowerCase();
            this.errorType = detail.includes('disconnect')
              ? 'disconnected'
              : 'stale';
            this.errorMessage =
              this.errorType === 'disconnected'
                ? 'Analytics integration is disconnected.'
                : 'Dashboard could not be found.';
          } else {
            this.errorType = 'generic';
            this.errorMessage = 'Failed to load dashboard.';
          }
        }
      });
  }

  private getUser(): any {
    return JSON.parse(
      localStorage.getItem('userData') || localStorage.getItem('user') || '{}'
    );
  }
}
